import ListPlace from "@/utils/presenters/ListPlace.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {ref, Ref, shallowRef, ShallowRef, toValue, watch} from "vue";
import {ColDef, ColGroupDef, GridApi} from "ag-grid-community";
import SortType from "@/utils/constants/SortType.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import {QSelectOption, QTreeNode} from "quasar";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import CriteriaTreeUtils from "@/utils/common/CriteriaTreeUtils.ts";
import {CriteriaExpressionSerializer, QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import RouteNames from "@/router/RouteNames.ts";
import router from "@/router";
import ListReportPlace from "@/utils/presenters/ListReportPlace.ts";
import CriteriaBuilder from "@/utils/criteria/CriteriaBuilder.ts";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import NamaPrinting from "@/utils/common/NamaPrinting.ts";
import OutputFormat from "@/utils/constants/OutputFormat.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import CacheUtil from "@/utils/login/CacheUtil.ts";
import EntityTypesUtils from "@/utils/common/EntityTypesUtils.ts";
import TreeViewPresenter from "@/utils/presenters/TreeViewPresenter.ts";
import CriteriaUtils from "@/utils/criteria/CriteriaUtils.ts";
import HierarchyFilterHelper from "@/utils/common/HierarchyFilterHelper.ts";
import CommonEntityViewCustomizer from "@/utils/UIConstants/CommonEntityViewCustomizer.ts";
import TreeModelProvider from "@/utils/common/TreeModelProvider.ts";
import ACCEntities from "@/utils/constants/ACCEntities.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import {FilterModel} from "ag-grid-enterprise";
import MetaData from "@/utils/common/MetaData.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import {useToastManager} from "@/stores/ToastsComposable.ts";
import {LocationQuery, RouteLocationNamedRaw} from "vue-router";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import QuickFilterUtils from "@/utils/common/QuickFilterUtils.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import CurrentUserUtils from "@/utils/login/CurrentUserUtils.ts";
import ListScreenLayout = namaTypes.ListScreenLayout;
import AGGridListRequest = namaTypes.AGGridListRequest;
import DTOCriteria = namaTypes.DTOCriteria;
import CustomRepInfo = namaTypes.CustomRepInfo;
import CriteriaField = namaTypes.CriteriaField;
import QuestionField = namaTypes.QuestionField;
import AGGridGetRowsRequest = namaTypes.AGGridGetRowsRequest;
import ListViewQuickFilterGroupVal = namaTypes.ListViewQuickFilterGroupVal;
import GUIActionResult = namaTypes.GUIActionResult;
import ListViewQuickFilterSelectedValues = namaTypes.ListViewQuickFilterSelectedValues;
import GCFailure = namaTypes.GCFailure;
import ViewName = namaTypes.ViewName;

export default class ListPresenter extends AbsPresenter
{
  preferences: namaTypes.UIPreferences = {};
  private _columnDefs: Ref<(ColDef | ColGroupDef)[]> = ref([]);
  gridApi: ShallowRef<GridApi | undefined> = shallowRef();
  firstDataLoaded: Ref<boolean> = ref(false);
  static readonly EXTRA_CRITERIA = "extraCriteria";
  parentFieldTitle: string = '';
  criteriaTree: Ref<QTreeNode<CustomDTOExpression>[]> = ref([]);
  listViewNameValue: Ref<QSelectOption> = ref({} as QSelectOption);
  sortFieldsValue: Ref<Record<string, string>[]> = ref([]);
  sortDirectionValue: Ref<QSelectOption> = ref({} as QSelectOption);
  quickFilterGroup: Ref<ListViewQuickFilterGroupVal[]> = ref([]);
  selectedQuickFilterGroups: Ref<ListViewQuickFilterSelectedValues[]> = ref([]);
  preventAutoLoad: Ref<boolean> = ref(false);
  sortOptions: Record<string, string>[] = [];
  availableCriteriaFields: CriteriaField[] = [];
  availableQuestionFields: QuestionField[] = [];
  listViewCriteria: DTOCriteria | undefined;
  treeView?: TreeViewPresenter;
  calledFromRefresh: boolean = false;
  ignoreNextRouteChange: boolean = false;
  constructor(query: Record<string, string | undefined>, path?: string) {
    super((path && path.substring(1) === RouteNames.REPORT_LIST_ROUTE) || query[PlaceTokens.PRM_LIST_VIEW_NAME] === "ReportCatalogue" ? new ListReportPlace(query) : new ListPlace(query));
    watch(toValue(this.selectedQuickFilterGroups), (__) => {
      if (ObjectChecker.isEmptyOrNull(this.quickFilterGroup.value))
        return;
      QuickFilterUtils.syncQuickFiltersToURL(this);
      this.gridApi.value?.refreshServerSide({purge: true});
    })
  }

  get queriedClassSimpleName() {
    const queriedClass = this.layout?.queriedClass;
    if (!queriedClass)
      return;
    return queriedClass.substring(queriedClass.lastIndexOf('.') + 1)
  }

  override fetchDefaultPageSize(): string | undefined {
    if (this.layout && this.layout.listViewDefaultPageSize)
      return this.layout.listViewDefaultPageSize;
    return CurrentUserUtils.defaultPageSize();
  }

  override fetchPlace(): ListPlace | ListReportPlace {
    return this.place as ListPlace | ListReportPlace;
  }

  override fetchMetadata(): namaTypes.NaMaMetaData {
    return this.preferences.metaData!;
  }

  override getDefaultCriteria(fieldId?: string): DTOCriteria | undefined {
    return this.layout?.defaultCriteria;
  }

  override fetchNodeIdFromPlace(): string | undefined {
    return this.fetchPlace().src;
  }

  override setTreeView(treeViewPresenter: TreeViewPresenter): void {
    this.treeView = treeViewPresenter;
  }

  override onTreeNodeSelection() {
    this.calledFromRefresh = true;
    this.gridApi.value?.refreshServerSide({purge: true});
  }

  override updateSelectedNodeId(value: string | undefined | null): void {
    if (this.treeView) {
      this.treeView.selectedNodeId.value = value;
      this.treeView.nodeSelectionEvent();
    }
  }

  override getHelperForTreeView(): HierarchyFilterHelper | undefined {
    return CommonEntityViewCustomizer.getHelperForListView(this.entityType);
  }

  override getTreeModelProviderForTreeView(): TreeModelProvider | undefined {
    let entityType = this.entityType;
    if (ACCEntities.Account === entityType)
      return new TreeModelProvider(ACCEntities.AccountsChart);
    if (BSCEntities.DMSDocument === entityType)
      return new TreeModelProvider(BSCEntities.DMSFolder);
    if (BSCEntities.Remark === entityType)
      return new TreeModelProvider(BSCEntities.RemarkType);
    if (BSCEntities.DetailedRemark === entityType)
      return new TreeModelProvider(BSCEntities.RemarkType);
    if (ObjectChecker.isAnyEqualToFirst(entityType, IdsAndConstants.AUTestDef))
      return new TreeModelProvider(IdsAndConstants.AUPhase);
    if (NamaCommon.hasParentEntity(entityType))
      return new TreeModelProvider(entityType);
    if (EntityTypesUtils.isMaster(entityType))
      return new TreeModelProvider(DomainBaseEntities.MasterGroup, entityType);
    return undefined;
  }

  async start() {
    this.preferences = await ListViewUtils.fetchLayoutPreferences(this.fetchPlace());
    this.initCriteriaAndQuestionFields();
    this.fetchPlace().ascending = this.isAscendingSort();
    this.fetchPlace().iconCode = ClientCache.getMenuLineBySrc(this.fetchPlace().src)?.iconCode ?? ''
    this.fetchPlace().title = Translator.getListViewTitle(this.entityType!, this.fetchPlace().listViewName, this.layout
      , ClientCache.getMenuLineBySrc(this.fetchPlace().src)) ?? '';
    this.fetchPlace().orderBy = this.getSortBy();
    let reportListView = this.place instanceof ListReportPlace;
    this._columnDefs.value = ListViewUtils.createDisplayColumns(this.layout?.displayColumns!, this.entityType, {
      metaData: this.preferences.metaData,
      listLayoutPreferences: this.preferences.listLayoutPreferences,
      isReportListView: reportListView,
      appendRowNumberToSelectCheckbox: true,
      layout: this.layout,
      presenter: this
    });
    await this.initQuickFilterGroups();
    QuickFilterUtils.restoreQuickFiltersFromURL(this);
    let extraCriteria: any;
    if (this.fetchPlace().extraCriteria)
      extraCriteria = await this.fetchExtraCriteriaData(this.fetchPlace().extraCriteria!);
    this.record.value[ListPresenter.EXTRA_CRITERIA] = extraCriteria;
    this.listViewCriteria = this.getCriteria();
    this.criteriaTree.value = CriteriaTreeUtils.criteriaToTree(this.listViewCriteria, this.availableCriteriaFields);
    this.updateWindowTitle();
    this.sortOptions = this.getSortByList();
    this.listViewNameValue.value = ListViewUtils.getListViewName(this.viewNames).find(l =>
      l.value === (this.fetchPlace().listViewName ?? StringConstants.DEFAULT)) ?? {} as QSelectOption;
    this.sortFieldsValue.value = ListViewUtils.fetchCurrentSortValueList(this.sortOptions, this.getSortBy());
    this.sortDirectionValue.value = ListViewUtils.sortDirections().find(l =>
      l.value === (this.fetchPlace().ascending ? ListViewUtils.ASC : ListViewUtils.DESC))!;
    this.preventAutoLoad.value = this.layout?.listViewPreventAutoLoad ?? false;
  }

  getCriteria(): DTOCriteria | undefined {
    let criteria = CriteriaTreeUtils.normalizeCachedCriteria(ListViewUtils.getListViewCriteria(this.fetchPlace()));
    return CriteriaUtils.addDefaultCriteriaTo(this.getDefaultCriteria(), criteria, this.layout?.listViewAlwaysApplyDefaultCriteria);
  }

  updateWindowTitle() {
    if (this.shouldUpdateWindowTitle())
      GUIUtilities.updateWindowTitle(this.place?.getTitleForWindow() ?? '');
  }

  isShowingTree(): boolean {
    if (!this.fetchPlace().showTree && this.layout && this.layout.showTreeViewByDefault)
      return true;
    return this.fetchPlace().showTree ?? false;
  }

  hasTree() {
    return this.isShowingTree() && EntityTypesUtils.isMaster(this.entityType);
  }

  shouldUpdateWindowTitle() {
    return true;
  }

  initCriteriaAndQuestionFields() {
    this.availableCriteriaFields = (this.layout?.criteriaFields ?? []).sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''));
    this.availableQuestionFields = this.layout?.questionFields ?? [];
  }

  get columnDefs(): (ColDef | ColGroupDef)[] {
    return this._columnDefs.value;
  }

  get layout(): ListScreenLayout | undefined {
    return this.preferences.listLayout;
  }

  get viewNames(): ViewName[] | undefined {
    return this.preferences?.viewNames;
  }

  isAscendingSort() {
    if (!this.layout?.defaultSortType)
      return !!(this.fetchPlace().ascending);
    return this.layout?.defaultSortType === SortType.Ascending;
  }

  fetchDisplayedColumns(): string[] {
    if (this.gridApi.value && !this.gridApi.value.isDestroyed())
      return this.gridApi.value.getAllDisplayedColumns().filter(l =>
        l.getColId() !== "select" && l.getColId() !== "colNumber").map(l => l.getColId());
    let cachedDisplayColumns = this.fetchPlace().getDisplayColumns();
    if (cachedDisplayColumns && cachedDisplayColumns?.length)
      return cachedDisplayColumns!
    if (this.columnDefs.length)
      return this.columnDefs.flatMap(l => ListViewUtils.childrenOfCol(l)).filter(l =>
        (l as ColDef).colId !== "select" && (l as ColDef).colId !== "colNumber").map(l => l.colId!);
    return [];
  }

  async listDataForTimeline(page: number, pageSize: number) {
    const request: AGGridGetRowsRequest = {page, pageSize, forTimeline: true, showLoadingIndicator: true};
    return await this.createServerSideDatasource().getRows({request});
  }

  createServerSideDatasource() {
    return {
      getRows: async (params) => {
        this.listViewCriteria = CriteriaTreeUtils.updateCriteriaFromTree(this.listViewCriteria, this.criteriaTree.value, this.availableCriteriaFields);
        await this.silentlyUpdateCriteriaInURL();
        const filterModelFieldsType: Record<string, string | undefined> = {};
        if (this.preventAutoLoad.value)
          return this.createEmptyList(params);
        if (params.request && params.request.filterModel) {
          const agGridFilterModel = params.request.filterModel as FilterModel;
          for (const field in agGridFilterModel) {
            let fieldMetadata = MetaData.findFieldMetadata(field, this.fetchMetadata());
            if (fieldMetadata) {
              filterModelFieldsType[field] = fieldMetadata.fieldType;
            }
            if (!filterModelFieldsType[field]) {
              let criteriaField = this.layout?.criteriaFields?.find(cf => cf.id === field);
              if (criteriaField)
                filterModelFieldsType[field] = criteriaField.metaData?.fieldType || criteriaField.fieldType;
            }
            if (!filterModelFieldsType[field]) {
              let column = this.layout?.displayColumns?.find(c => c.id === field);
              if (column)
                filterModelFieldsType[field] = column.metaData?.fieldType;
            }
          }
        }

        let request: AGGridListRequest = this.getListRequest(params, filterModelFieldsType);
        let settings: NamaAxiosRequestConfig = {
          data: request,
          noLoadingIndicator: !this.calledFromRefresh && !params.request.showLoadingIndicator,
        }
        ListViewUtils.updateCache(request, this.layout?.id);
        let data = await NamaCommon.namaFetch(NamaURLS.LIST_AG_GRID_ENTITIES, settings);
        this.calledFromRefresh = false;
        if (data) {
          let listResult = data as namaTypes.ListEntityResult;
          const route: RouteLocationNamedRaw = {
            name: router.currentRoute.value.name,
            query: this.fetchPlace().fetchQuery()
          };
          if (listResult && listResult.result?.succeded) {
            if (this.isCurrentRouteAListRoute())
              GUIUtilities.updateRecentVisits(route)
            let rows: any[] = [];
            if (listResult.page && listResult.page.length) {
              let dtos = listResult.page as namaTypes.DTOTabularResult[];
              if (params.request.forTimeline)
                rows = dtos
              else
                rows = dtos.map(l => l.values)
            }
            let startIndex = params.request.startIndex ?? 0;
            for (let i = 0; i < rows.length; i++) {
              rows[i].namaRowIndex = (i + startIndex + 100).toString();
            }
            const response = {
              rowData: rows,
              rowCount: listResult.totalRecordsCount,
              pageNumber: listResult.pageNumber,
              pageSize: listResult.pageSize,
            };
            if (params.success)
              params.success(response);
            this.firstDataLoaded.value = true;
            return response;
          } else if (listResult && (listResult.result?.failed || (listResult as GCFailure).failureOccurred)) {
            //todo
            GUIUtilities.deleteRecentVisit(route);
          }
        } else {
          useErrorsStore().pushErrorStr("Server Error");
        }
      },
    };
  }

  private createEmptyList(params) {
    let emptyRows = {
      rowData: [],
      rowCount: 0,
      pageNumber: 0,
      pageSize: 0
    };
    if (params.success)
      params.success(emptyRows);
    this.firstDataLoaded.value = true;
    return emptyRows;
  }

  private getListRequest(params?: any, filterModelFieldsType?: Record<string, string | undefined>): AGGridListRequest {
    const getTreeSelectedNodeId = () => {
      if ((this.fetchPlace() as any)?.rootType === BSCEntities.ReportGroup)
        return undefined;
      else
        return this.fetchTreeSelectedNodeId();
    }
    return {
      entityType: this.entityType,
      owner: {
        ownerType: "Entity",
        ownerId: this.entityType
      },
      listId: this.fetchPlace().listViewName,
      displayedColumns: this.fetchDisplayedColumns(),
      agGridRequest: params.request,
      extraCriteria: this.fetchExtraCriteria(),
      treeSelectedNodeId: getTreeSelectedNodeId(),
      extraCriteriaId: this.fetchPlace().extraCriteria,
      menuItemExtraCriteriaId: EditPresenter.getMenuViewExtraCriteriaId(this.fetchPlace().src),
      criteria: this.listViewCriteria,
      orderBy: this.fetchPlace().orderBy,
      ascending: this.fetchPlace().ascending,
      questionFields: this.fetchFilledQuestionFields(this.layout?.questionFields ?? []),
      filterModelFieldsType: filterModelFieldsType,
      selectedQuickFilterGroups: this.selectedQuickFilterGroups.value
    };
  }

  fetchTreeSelectedNodeId(): string | undefined {
    if (this.hasTree() && this.treeView && this.treeView.selectedNodeId.value)
      return this.treeView.selectedNodeId.value;
    if (!(this.fetchPlace() instanceof ListReportPlace))
      return undefined;
    let listPlace = this.fetchPlace() as ListReportPlace;
    if (listPlace.rootType === DomainBaseEntities.MasterGroup && listPlace.src && listPlace.src.replaceAll("-", "").length === 32)
      return listPlace.src;
  }

  fetchExtraCriteria(): DTOCriteria {
    let reportDefinitionGroupCriteria: DTOCriteria | undefined = this.getReportDefinitionGroupCriteria();
    return CriteriaUtils.joinCriteria(reportDefinitionGroupCriteria ?? {});
  }

  getTreeViewExtraCriteria(): DTOCriteria | undefined {
    if (this.hasTree() && this.treeView) {
      let criteria: DTOCriteria | undefined = this.treeView.getCriteria();
      if (criteria)
        criteria.ownerType = this.entityType;
      return criteria;
    }
    return undefined;
  }

  getReportDefinitionGroupCriteria(): DTOCriteria | undefined {
    if ((this.fetchPlace() as ListReportPlace).rootType !== BSCEntities.ReportGroup)
      return;
    return CriteriaBuilder.create().field(DomainBaseFieldIDs.ReportDefinition_groupsIds)
      .contains((this.fetchPlace() as ListReportPlace).currentRoot?.replaceAll("-", "")).build();
  }

  private static readonly LIST_ROUTE_NAMES: (string | symbol)[] = [RouteNames.LIST_VIEW_ROUTE, RouteNames.REPORT_LIST_ROUTE];

  private isCurrentRouteAListRoute(): boolean {
    const currentName = router.currentRoute.value.name;
    return !!currentName && ListPresenter.LIST_ROUTE_NAMES.includes(currentName);
  }

  async silentlyUpdateCriteriaInURL() {
    if (!this.isCurrentRouteAListRoute())
      return;
    this.ignoreNextRouteChange = true;
    this.fetchPlace().criteria = new CriteriaExpressionSerializer().toURL(this.listViewCriteria);
    const query: LocationQuery = this.fetchPlace().fetchQuery() as LocationQuery;
    await router.push({
      name: router.currentRoute.value.name,
      query
    });
    this.ignoreNextRouteChange = false;
  }

  fetchInitialGridState() {
    return ListViewUtils.fetchGridStateFromStorage(this);
  }

  getSortByList() {
    let listLayout = this.preferences.listLayout ?? {};
    if (!listLayout.sortColumns) {
      return [
        {
          label: Translator.translateId(CommonFieldIds.CODE, this.fetchPlace().entityType),
          value: CommonFieldIds.CODE,
          searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue(CommonFieldIds.CODE, this.fetchPlace().entityType!)
        }, {
          label: Translator.translateId(CommonFieldIds.CREATION_DATE, this.fetchPlace().entityType),
          value: CommonFieldIds.CREATION_DATE,
          searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue(CommonFieldIds.CREATION_DATE, this.fetchPlace().entityType!)
        },
      ]
    } else {
      let sortByList: Record<string, string>[] = [];
      for (let column of listLayout.sortColumns)
        sortByList.push({
          value: column.id!,
          label: Translator.translateTitledId(this.fetchPlace().entityType, column)!,
          searchData: EditScreenFieldUtils.fetchSearchTextForAllowedValue(column.id!, this.fetchPlace().entityType!)
        });
      return sortByList;
    }
  }

  getSortBy() {
    let placeSortBy = this.fetchPlace().orderBy;
    if (!placeSortBy && this.preferences.listLayout)
      placeSortBy = this.preferences.listLayoutPreferences?.sortBy;
    if (!placeSortBy)
      placeSortBy = this.preferences.listLayout?.defaultOrderBy;
    return placeSortBy ?? "";
  }

  async fetchExtraCriteriaData(extraCriteriaId: string) {
    if (!extraCriteriaId)
      return;
    let extraCrtInRecord = this.record.value[ListPresenter.EXTRA_CRITERIA];
    if (extraCrtInRecord && extraCrtInRecord.id === extraCriteriaId)
      return extraCrtInRecord;
    let extraCriteria = await NamaCommon.namaFetch(NamaURLS.FIND_REF_BY_ID, {
      params: {
        entityType: BSCEntities.CriteriaDefinition,
        id: extraCriteriaId
      }
    });
    if (!NamaCommon.isSuccess(extraCriteria))
      extraCriteria = undefined;
    return extraCriteria;
  }

  updateCriteriaFromPlace() {
    this.listViewCriteria = new CriteriaExpressionSerializer().criteriaFromURL(this.fetchPlace().criteria);
    if (ObjectChecker.isEmptyOrNull(this.criteriaTree.value?.[0]?.children))
      this.criteriaTree.value = CriteriaTreeUtils.criteriaToTree(this.listViewCriteria, this.availableCriteriaFields);
  }

  printUsing(customRepInfo: CustomRepInfo) {
    let ids: string[];
    if (customRepInfo.selectedRecordsOnly)
      ids = AGGridUtils.fetchSelectedRows(this.gridApi.value!).map(l => l[CommonFieldIds.ID]);
    else
      ids = AGGridUtils.getCurrentPageData(this.gridApi.value!).map(l => l[CommonFieldIds.ID]);
    NamaPrinting.printList(this.entityType, ids, OutputFormat.PDF, this.layout?.id, 0, customRepInfo.reportId);
  }

  override fetchParentFieldTitle(): string {
    return this.parentFieldTitle;
  }

  fetchFilledQuestionFields(list: QuestionField[]) {
    if (!this.fetchPlace().questions)
      return CacheUtil.getCachedQuestionFields(this.entityType, this.fetchPlace().listViewName);
    else
      return new QuestionFieldsSerializer().questionsFromURL(this.fetchPlace().questions);
  }

  showErrorsExecuteFn() {
    useToastManager().showErrorsToast();
  };

  async onCustomActionSuccess(result: GUIActionResult): Promise<void> {
    await ActionPerformerUtil.processGUIActionResult(result, -1, this.fetchDispatcher());
  }

  async initQuickFilterGroups() {
    const quickFilterSelectedValues = this.selectedQuickFilterGroups.value;
    quickFilterSelectedValues.splice(0, quickFilterSelectedValues.length);
    let request: AGGridListRequest = this.getListRequest({});
    const settings: NamaAxiosRequestConfig = {
      data: {
        request
      },
      noLoadingIndicator: true
    }
    const data = await NamaCommon.namaFetch(NamaURLS.FETCH_QUICK_FILTER_VALUES, settings)
    if (NamaCommon.isSuccess(data))
      this.quickFilterGroup.value = data.list as ListViewQuickFilterGroupVal[];
  }

  async removeFilter(api: GridApi) {
    if (this.place && this.place instanceof ListPlace)
      this.place.criteria = '';
    this.listViewCriteria = undefined;
    this.criteriaTree.value = CriteriaTreeUtils.criteriaToTree(undefined, this.availableCriteriaFields);
    if (api && !api.isDestroyed())
      api.setFilterModel(null);
    this.selectedQuickFilterGroups.value = [];
    ClientCache.uiPreferencesCache[this.entityType] = {};
    this.ignoreNextRouteChange = true;
    await router.push({
      name: RouteNames.LIST_VIEW_ROUTE,
      query: this.fetchPlace().fetchQuery(),
    });
    this.preventAutoLoad.value = false;
    this.ignoreNextRouteChange = false;
    if (api && !api.isDestroyed())
      api.refreshServerSide({purge: true});
  }
}
